import { useState, useEffect } from "react";
import { X, Calendar, RefreshCw, Plus, Clock, Video, Bot, ChevronRight, Sparkles } from "lucide-react";
import getApiUrl from './utils/getApiUrl';

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


const API_BASE = getApiUrl();

// Render functions to avoid Babel plugin issue with inline .map()
function renderReunionCards(reunions, agents, selected, onSelect, now) {
  const cards = [];
  for (let i = 0; i < reunions.length; i++) {
    const reunion = reunions[i];
    let agent = null;
    for (let j = 0; j < agents.length; j++) {
      if (agents[j].director_id === reunion.pole_id) {
        agent = agents[j];
        break;
      }
    }
    const d = new Date(reunion.scheduled_date);
    const avatar = reunion.agent_avatar || (agent ? agent.avatar : "🤖");
    const name = reunion.agent_name || (agent ? agent.name : "Agent IA");
    const isSelected = selected && selected._id === reunion._id;
    
    cards.push(
      <div 
        key={reunion._id}
        onClick={() => onSelect(reunion)}
        className={isSelected 
          ? "bg-slate-800/50 border border-purple-500 ring-1 ring-purple-500/30 rounded-xl p-5 cursor-pointer" 
          : "bg-slate-800/50 border border-slate-700/50 rounded-xl p-5 cursor-pointer hover:border-purple-500/50"
        }
      >
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <span className="text-4xl">{avatar}</span>
            <div>
              <h3 className="font-semibold text-lg">{reunion.title}</h3>
              <p className="text-sm text-slate-400">{name}</p>
              <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {d.toLocaleDateString("fr-FR")}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                </span>
              </div>
            </div>
          </div>
          <span className={reunion.status === "completed" ? "px-2 py-1 rounded text-xs bg-green-500/20 text-green-400" : "px-2 py-1 rounded text-xs bg-blue-500/20 text-blue-400"}>
            {reunion.status === "completed" ? "Terminée" : "Planifiée"}
          </span>
        </div>
      </div>
    );
  }
  return cards;
}

function renderAgentOptions(agents) {
  const options = [<option key="_empty" value="">Sélectionner...</option>];
  for (let i = 0; i < agents.length; i++) {
    const a = agents[i];
    if (a.status === "active") {
      options.push(
        <option key={a.director_id} value={a.director_id}>{a.avatar} {a.name}</option>
      );
    }
  }
  return options;
}

function renderAgentsList(agents) {
  const items = [];
  for (let i = 0; i < agents.length; i++) {
    const a = agents[i];
    if (a.status === "active") {
      items.push(
        <div key={a.director_id} className="flex items-center gap-3 p-2 hover:bg-slate-700/30 rounded-lg">
          <span className="text-xl">{a.avatar}</span>
          <div>
            <p className="text-sm font-medium">{a.name}</p>
            <p className="text-xs text-slate-500">{a.pole}</p>
          </div>
        </div>
      );
    }
  }
  return items;
}

function renderTopics(topics) {
  const items = [];
  for (let i = 0; i < topics.length; i++) {
    items.push(
      <div key={i} className="flex items-start gap-2 text-sm">
        <ChevronRight className="w-4 h-4 text-purple-400 mt-0.5" />
        {topics[i]}
      </div>
    );
  }
  return items;
}

export default function ReunionRoom({ onBack }) {
  const [reunions, setReunions] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("upcoming");
  const [formPoleId, setFormPoleId] = useState("");
  const [formTitle, setFormTitle] = useState("");
  const [formDate, setFormDate] = useState("");
  const [formTime, setFormTime] = useState("10:00");
  const [formTopics, setFormTopics] = useState("");

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const agentsRes = await authFetch(API_BASE + "/api/direction/agents");
      if (agentsRes.ok) {
        const data = await agentsRes.json();
        setAgents(data.agents || []);
      }
      const reunionsRes = await authFetch(API_BASE + "/api/reunions?limit=50");
      if (reunionsRes.ok) {
        const data = await reunionsRes.json();
        setReunions(data.reunions || []);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleCreate = async () => {
    if (!formPoleId || !formTitle || !formDate) {
      alert("Remplissez tous les champs obligatoires");
      return;
    }
    try {
      await authFetch(API_BASE + "/api/reunions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pole_id: formPoleId,
          title: formTitle,
          scheduled_date: formDate + "T" + formTime + ":00",
          reunion_type: "weekly",
          topics: formTopics.split("\n").filter(Boolean)
        })
      });
      setShowModal(false);
      setFormPoleId("");
      setFormTitle("");
      setFormDate("");
      setFormTime("10:00");
      setFormTopics("");
      loadData();
    } catch (e) { console.error(e); }
  };

  const handleGenerateSummary = async (id) => {
    try {
      await authFetch(API_BASE + "/api/reunions/" + id + "/generate-summary", { method: "POST" });
      loadData();
    } catch (e) { console.error(e); }
  };

  const now = new Date();
  const upcomingReunions = [];
  const pastReunions = [];
  for (let i = 0; i < reunions.length; i++) {
    const r = reunions[i];
    if (new Date(r.scheduled_date) >= now) {
      upcomingReunions.push(r);
    } else {
      pastReunions.push(r);
    }
  }
  const displayList = tab === "upcoming" ? upcomingReunions : pastReunions;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white">
      <header className="sticky top-0 z-40 bg-slate-900/95 backdrop-blur-sm border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={onBack} className="p-2 hover:bg-slate-800 rounded-lg" data-testid="reunion-back-btn">
                <X className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold">🏛️ Salle de Réunion Virtuelle</h1>
                  <p className="text-sm text-slate-400">Réunions avec les responsables IA</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => setShowModal(true)} className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg" data-testid="new-reunion-btn">
                <Plus className="w-4 h-4" />Planifier
              </button>
              <button onClick={loadData} disabled={loading} className="p-2 bg-slate-800 hover:bg-slate-700 rounded-lg">
                <RefreshCw className={loading ? "w-4 h-4 animate-spin" : "w-4 h-4"} />
              </button>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button 
              onClick={() => setTab("upcoming")} 
              className={tab === "upcoming" ? "px-4 py-2 bg-purple-500/20 text-purple-400 border border-purple-500/30 rounded-lg text-sm flex items-center gap-2" : "px-4 py-2 bg-slate-800/50 text-slate-400 rounded-lg text-sm flex items-center gap-2"}
              data-testid="tab-upcoming"
            >
              <Clock className="w-4 h-4" />À venir ({upcomingReunions.length})
            </button>
            <button 
              onClick={() => setTab("past")} 
              className={tab === "past" ? "px-4 py-2 bg-purple-500/20 text-purple-400 border border-purple-500/30 rounded-lg text-sm" : "px-4 py-2 bg-slate-800/50 text-slate-400 rounded-lg text-sm"}
              data-testid="tab-past"
            >
              Passées ({pastReunions.length})
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-8 h-8 text-purple-400 animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              {displayList.length === 0 ? (
                <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-8 text-center">
                  <Calendar className="w-12 h-12 text-slate-500 mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">Aucune réunion</h3>
                  <button onClick={() => setShowModal(true)} className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm mt-2">
                    Planifier une réunion
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {renderReunionCards(displayList, agents, selected, setSelected, now)}
                </div>
              )}
            </div>
            <div className="space-y-4">
              {selected ? (
                <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5 sticky top-28">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold">Détails</h3>
                    <button onClick={() => setSelected(null)} className="p-1 hover:bg-slate-700 rounded">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="font-medium mb-2">{selected.title}</p>
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-2xl">{selected.agent_avatar}</span>
                    <span>{selected.agent_name}</span>
                  </div>
                  {selected.topics && selected.topics.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs text-slate-500 mb-2">Sujets</p>
                      {renderTopics(selected.topics)}
                    </div>
                  )}
                  {selected.compte_rendu ? (
                    <div className="bg-slate-900/50 rounded-lg p-3 text-sm whitespace-pre-wrap max-h-48 overflow-y-auto">
                      {selected.compte_rendu}
                    </div>
                  ) : new Date(selected.scheduled_date) < now ? (
                    <button onClick={() => handleGenerateSummary(selected._id)} className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm">
                      <Sparkles className="w-4 h-4" />Générer compte-rendu IA
                    </button>
                  ) : null}
                </div>
              ) : (
                <div className="bg-slate-800/30 border border-dashed border-slate-700/50 rounded-xl p-8 text-center">
                  <Video className="w-10 h-10 text-slate-500 mx-auto mb-3" />
                  <p className="text-slate-400 text-sm">Sélectionnez une réunion</p>
                </div>
              )}
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Bot className="w-5 h-5 text-purple-400" />Responsables IA
                </h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {renderAgentsList(agents)}
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-lg">
            <div className="p-6 border-b border-slate-700 flex items-center justify-between">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <Calendar className="w-6 h-6 text-purple-400" />Planifier une réunion
              </h3>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-slate-800 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">Responsable IA *</label>
                <select value={formPoleId} onChange={e => setFormPoleId(e.target.value)} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2">
                  {renderAgentOptions(agents)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">Titre *</label>
                <input type="text" value={formTitle} onChange={e => setFormTitle(e.target.value)} placeholder="Ex: Bilan hebdomadaire" className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">Date *</label>
                  <input type="date" value={formDate} onChange={e => setFormDate(e.target.value)} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">Heure</label>
                  <input type="time" value={formTime} onChange={e => setFormTime(e.target.value)} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">Sujets (un par ligne)</label>
                <textarea value={formTopics} onChange={e => setFormTopics(e.target.value)} rows={3} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2" />
              </div>
            </div>
            <div className="p-6 border-t border-slate-700 flex justify-end gap-3">
              <button onClick={() => setShowModal(false)} className="px-6 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg">Annuler</button>
              <button onClick={handleCreate} className="px-6 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg flex items-center gap-2">
                <Plus className="w-4 h-4" />Planifier
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
